/**
 * Контракты режима «Свой текст» (ТЗ §6, режим 13).
 *
 * Материал приносит пользователь, и главное требование к режиму — не
 * материал, а приватность: текст разбирается на устройстве и с него не
 * уходит. На сервер отправляются только агрегаты, а единицы этого режима не
 * синхронизируются вовсе (ТЗ §11.2, решение AD-004).
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  OsPlatform,
} from '@type2x/shared-contracts';
import type { Exercise } from '@type2x/exercise-generator';

/** Снимок навыка, из которого строится занятие. */
export interface CustomTextSnapshot {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /**
   * Операционная система: от неё зависит состав раскладки.
   *
   * Слово отбрасывается по символам, которых в раскладке нет, а на macOS
   * русская раскладка их набирает не те же самые (ТЗ FR-EN-42).
   */
  readonly platform: OsPlatform;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Слово пользовательского текста. */
export interface CustomWord {
  readonly text: string;
  /** Сколько раз слово встретилось в тексте. */
  readonly count: number;
  /** Доля слова в тексте, 0..1: частота считается по самому тексту. */
  readonly frequency: number;
}

/** Разбор пользовательского текста. */
export interface ParsedText {
  /** Слова текста в порядке убывания частоты внутри него. */
  readonly words: readonly CustomWord[];
  /** Сколько слов отброшено: в них есть символы, которых нет в раскладке. */
  readonly rejectedWords: number;
  /** Символы текста, которых нет в раскладке, в порядке первой встречи. */
  readonly unsupportedCharacters: readonly string[];
  /** Хватает ли материала на упражнение. */
  readonly usable: boolean;
}

/** Занятие режима. */
export interface CustomTextPlan {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly exercise: Exercise;
  /** Учебные единицы слов текста: они остаются на устройстве. */
  readonly units: readonly MasteryUnit[];
  readonly hintLevel: HintLevel;
  /** Сколько слов текста не годится для набора в этой раскладке. */
  readonly rejectedWords: number;
  readonly unsupportedCharacters: readonly string[];
}

/**
 * Приставка идентификатора единицы этого режима.
 *
 * По ней слой синхронизации отличает материал пользователя от общего и не
 * отправляет его на сервер: идентификатор `word:договор` — это и есть кусок
 * пользовательского текста.
 */
export const CUSTOM_UNIT_PREFIX = 'custom:';

/**
 * Сколько разных слов нужно, чтобы занятие имело смысл.
 *
 * Из трёх слов упражнение выродится в повтор одного и того же, а повтор
 * тренирует не набор, а конкретную строку.
 */
export const MIN_WORDS = 8;

/**
 * Предел длины текста, символов.
 *
 * Разбор идёт на устройстве в обработчике, а не в фоне: книгу целиком он
 * разбирал бы заметное время. Хвост текста отбрасывается, о чём режим и
 * сообщает.
 */
export const MAX_TEXT_LENGTH = 20_000;

/** Максимальная длина слова: длиннее — это не слово, а склейка. */
export const MAX_WORD_LENGTH = 24;

/** Сколько элементов в упражнении режима. */
export const CUSTOM_TEXT_ITEMS = 24;

/** Уровень подсказок: материал знакомый, клавиатура нужна лишь при заминке. */
export const CUSTOM_TEXT_HINT_LEVEL: HintLevel = 'delayed-hint';
